/**
 * Las preguntas que este servicio le hace a quien reserva.
 *
 * La página de reserva pide siempre nombre, correo, teléfono y una nota libre.
 * Eso basta para cortarse el pelo y para poco más: la dentista necesita saber
 * si es primera visita, el estudio de tatuajes la zona y el tamaño. Sin esto
 * todo acababa dentro de la nota, sin poder exigirlo ni leerlo de un vistazo.
 *
 * Se guarda solo, campo a campo, como el resto de la pantalla —nadie espera un
 * botón de guardar en una pestaña donde todo lo demás se guarda al salir del
 * campo—. Cada cambio manda la lista ENTERA: es una sola columna `jsonb`, y
 * mandar parches por posición sobre una lista que se reordena es cómo se
 * termina guardando la etiqueta de un campo en otro.
 */
import { useEffect, useRef, useState } from 'react'
import {
  IconAlignLeft,
  IconAt,
  IconCheck,
  IconCheckbox,
  IconChevronDown,
  IconChevronUp,
  IconLetterCase,
  IconList,
  IconLoader2,
  IconPhone,
  IconPlus,
  IconTrash,
  type Icon,
} from '@tabler/icons-react'
import { BOOKING_FIELD_TYPES, type BookingFieldType, type BookingFormField } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

/** El tope que también aplica el worker. Enseñarlo evita el error por sorpresa. */
const MAX_FIELDS = 10
const MAX_OPTIONS = 20

/* Guarda la CLAVE, no el texto: es una constante de módulo y ahí no se puede
   llamar a un hook. Quien la usa la pasa por su propio `t`. */
const TYPE_KEY: Record<BookingFieldType, string> = {
  text: 'bookings.field.type.text',
  textarea: 'bookings.field.type.textarea',
  select: 'bookings.field.type.select',
  checkbox: 'bookings.field.type.checkbox',
  email: 'bookings.field.type.email',
  tel: 'bookings.field.type.tel',
}

/**
 * El dibujo de cada tipo, delante del desplegable.
 *
 * Un `<select>` no puede llevar iconos dentro de sus opciones —eso lo pinta el
 * sistema—, así que el del tipo elegido va fuera, a su izquierda. No sobra: la
 * palabra dice qué es y el icono dice de qué familia, y con diez preguntas
 * seguidas la columna de iconos se lee de un vistazo mientras que diez veces
 * «Texto corto» hay que leerlas una a una.
 */
export const TYPE_ICON: Record<BookingFieldType, Icon> = {
  text: IconLetterCase,
  textarea: IconAlignLeft,
  select: IconList,
  checkbox: IconCheckbox,
  email: IconAt,
  tel: IconPhone,
}


/**
 * El selector del tipo de pregunta.
 *
 * ── Por qué no un `<select>` ────────────────────────────────────────────────
 * Porque lo pinta el sistema y ahí dentro no cabe nada nuestro: ni el icono de
 * cada tipo, ni la marca de cuál está puesto. Y son seis opciones que se
 * distinguen por lo que HACEN —una casilla, una lista, un correo— así que verlas
 * dibujadas al elegir es la mitad de la decisión. Con el desplegable del sistema
 * el icono sólo podía ir fuera, junto al valor ya elegido, que es justo el
 * momento en que ya no hace falta.
 *
 * Es un menú y no una fila de botones: seis opciones puestas siempre a la vista
 * pesarían más que la pregunta, y sólo se cambian una vez.
 */
function SelectorDeTipo({ value, onChange }: {
  value: BookingFieldType
  onChange: (type: BookingFieldType) => void
}) {
  const { t } = useI18n()
  const [abierto, setAbierto] = useState(false)
  /* Hacia arriba cuando abajo no cabe: la lista de preguntas vive dentro de una
     columna que se desplaza, y la última tarjeta abría su menú contra el borde. */
  const [haciaArriba, setHaciaArriba] = useState(false)
  const caja = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!abierto) return
    const fuera = (event: MouseEvent) => {
      if (!caja.current?.contains(event.target as Node)) setAbierto(false)
    }
    const tecla = (event: KeyboardEvent) => { if (event.key === 'Escape') setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('keydown', tecla)
    return () => {
      document.removeEventListener('mousedown', fuera)
      document.removeEventListener('keydown', tecla)
    }
  }, [abierto])

  const abrir = () => {
    const caso = caja.current?.getBoundingClientRect()
    if (caso) setHaciaArriba(window.innerHeight - caso.bottom < 230)
    setAbierto((antes) => !antes)
  }

  const Actual = TYPE_ICON[value]
  return (
    <div className={`dash-booking-type-picker${haciaArriba ? ' is-up' : ''}`} ref={caja}>
      <button
        type="button"
        className="dash-booking-type-btn"
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-label={t('bookings.field.type')}
        onClick={abrir}
      >
        <Actual size={15} aria-hidden="true" />
        <span>{t(TYPE_KEY[value])}</span>
        <IconChevronDown size={13} aria-hidden="true" />
      </button>
      {abierto ? (
        <div className="dash-booking-type-menu" role="listbox" aria-label={t('bookings.field.type')}>
          {BOOKING_FIELD_TYPES.map((tipo) => {
            const Dibujo = TYPE_ICON[tipo]
            return (
              <button
                key={tipo}
                type="button"
                role="option"
                aria-selected={tipo === value}
                className={tipo === value ? 'is-on' : ''}
                onClick={() => { setAbierto(false); onChange(tipo) }}
              >
                <Dibujo size={16} aria-hidden="true" />
                <span>{t(TYPE_KEY[tipo])}</span>
                {tipo === value ? <IconCheck size={14} aria-hidden="true" /> : null}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/**
 * De la etiqueta al identificador que viaja en el formulario.
 *
 * Se calcula una sola vez, al crear la pregunta, y no vuelve a tocarse aunque
 * la etiqueta cambie: es la clave con la que se casa una respuesta, y moverla
 * bajo los pies de las citas ya hechas no arregla nada —la etiqueta ya viaja
 * congelada dentro de cada respuesta— y sí rompe el formulario de quien lo
 * tenga abierto en ese momento.
 */
function slugify(label: string, taken: Set<string>): string {
  const base = label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)
    || 'pregunta'

  if (!taken.has(base)) return base
  for (let n = 2; n < 100; n++) {
    const candidate = `${base}-${n}`.slice(0, 40)
    if (!taken.has(candidate)) return candidate
  }
  return `${base}-${Date.now().toString(36)}`.slice(0, 40)
}

/**
 * Si una pregunta está lista para irse al servidor.
 *
 * Recién añadida no lo está: nace sin etiqueta, y el servidor —con razón— no
 * acepta una pregunta que no pregunta nada. Un desplegable sin opciones es el
 * otro caso: se dibujaría vacío y, siendo obligatorio, dejaría la página
 * imposible de enviar.
 */
export function isCompleteFormField(field: BookingFormField): boolean {
  return field.label.trim() !== '' && (field.type !== 'select' || field.options.length > 0)
}

export function BookingFormFieldsEditor({ fields = [], saving, onChange, plain = false, onIncomplete, revealMissing = false }: {
  /** Puede faltar: un worker anterior a esta columna no manda el campo. */
  fields?: BookingFormField[] | undefined
  saving: boolean
  onChange: (fields: BookingFormField[]) => void
  /**
   * Cuántas preguntas están a medias, para quien pueda impedir seguir.
   *
   * Hace falta porque `onChange` manda sólo las COMPLETAS —una pregunta sin
   * texto no puede irse al servidor— y desde fuera eso es indistinguible de que
   * no exista: el asistente veía «cero preguntas» y dejaba pasar de largo
   * dejándose por el camino la que estabas escribiendo.
   */
  onIncomplete?: ((count: number) => void) | undefined
  /**
   * Enseñar ya lo que falta, sin esperar a que se salga del campo.
   *
   * En reposo, una pregunta recién añadida no se marca en rojo: sería una
   * alarma sobre algo que acabas de crear. Pero cuando alguien intenta avanzar
   * y no le dejan, la pregunta es «¿cuál?», y ahí sí hay que señalarlas todas.
   */
  revealMissing?: boolean | undefined
  /**
   * Dentro del asistente de alta, donde no hay rejilla de dos columnas ni
   * guardado por campo. La lista de preguntas es idéntica en los dos sitios;
   * lo único que cambia es el marco, y tener dos editores para eso acabaría
   * con un tipo de campo que sólo existe en uno.
   */
  plain?: boolean
}) {
  const { t } = useI18n()
  /**
   * Lo que se está escribiendo, sólo para las etiquetas y las opciones.
   *
   * El resto —tipo, obligatorio, orden— se guarda en cuanto se toca porque un
   * clic es una decisión terminada. El texto no: guardar en cada tecla mandaría
   * una petición por letra, así que se acumula aquí y sale al salir del campo.
   */
  /**
   * Borradores de las opciones mientras se escriben.
   *
   * Sólo las opciones. La etiqueta se guarda en cada tecla —la vista previa del
   * asistente la enseña mientras se escribe— pero una lista de opciones se
   * construye partiendo el texto por líneas, y hacerlo en cada tecla borra la
   * línea vacía justo cuando alguien acaba de pulsar Intro para escribir la
   * siguiente.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  /**
   * La lista tal como se está editando, incompletas incluidas.
   *
   * Existe porque en la ficha cada cambio se guarda al momento, y una pregunta
   * recién añadida todavía no tiene etiqueta: mandarla hacía que el servidor
   * rechazara el PATCH entero, así que el botón «Añadir pregunta» no llegaba a
   * funcionar nunca. Ahora la tarjeta aparece aquí y sólo sube cuando ya dice
   * algo. Lo que se manda hacia arriba es siempre lo válido y nada más.
   */
  const [rows, setRows] = useState<BookingFormField[]>(fields)
  /** Lo último que mandamos, para distinguir el eco del padre de un cambio suyo. */
  const sent = useRef<BookingFormField[]>(fields)

  useEffect(() => {
    if (JSON.stringify(fields) === JSON.stringify(sent.current)) return
    sent.current = fields
    setRows(fields)
  }, [fields])

  /**
   * Guarda la lista y sube al servidor sólo lo que ya está completo.
   *
   * `notify: false` para los cambios que dejan una pregunta a medias —pasarla a
   * «lista de opciones» antes de escribirlas, o vaciar su texto—. Sin eso, el
   * servidor recibía la lista sin ella, o sea que tocar el tipo de una pregunta
   * ya guardada la borraba mientras la terminabas. Lo último bueno se queda
   * guardado, y de lo que falta avisa el filo rojo del campo —antes era un
   * renglón de texto bajo cada tarjeta, y con diez preguntas eran diez—.
   */
  const commit = (next: BookingFormField[], notify = true) => {
    setRows(next)
    if (!notify) return
    const ready = next.filter(isCompleteFormField)
    if (JSON.stringify(ready) === JSON.stringify(sent.current)) return
    sent.current = ready
    onChange(ready)
  }

  /**
   * Las preguntas por las que ya se pasó.
   *
   * El filo rojo del campo vacío sólo aparece después de salir de él. Sin esto,
   * cada pregunta recién añadida nacía marcada en rojo: una alarma sobre algo
   * que acabas de crear y en lo que todavía no te ha dado tiempo a escribir.
   * Que avise cuando la dejas a medias es un aviso; que avise antes de empezar
   * es ruido, y enseña a no hacer caso del rojo.
   */
  const [tocadas, setTocadas] = useState<Set<string>>(() => new Set())

  /* Se cuenta desde `rows` y no dentro de `commit` porque hay cambios que no
     notifican —los que dejan una pregunta a medias— y son justamente los que
     hacen falta contar aquí. */
  useEffect(() => {
    onIncomplete?.(rows.filter((row) => !isCompleteFormField(row)).length)
  }, [rows, onIncomplete])

  const draftOf = (key: string, fallback: string) => drafts[key] ?? fallback
  const setDraft = (key: string, value: string) => setDrafts((current) => ({ ...current, [key]: value }))
  const clearDraft = (key: string) => setDrafts((current) => {
    if (!(key in current)) return current
    const rest = { ...current }
    delete rest[key]
    return rest
  })

  const replace = (index: number, patch: Partial<BookingFormField>) => {
    const next = rows.map((field, position) => (position === index ? { ...field, ...patch } : field))
    commit(next, isCompleteFormField(next[index]!))
  }

  const add = () => {
    const taken = new Set(rows.map((field) => field.name))
    commit([...rows, {
      name: slugify('pregunta', taken),
      label: '',
      type: 'text',
      required: false,
      options: [],
    }])
  }

  const remove = (index: number) => {
    commit(rows.filter((_, position) => position !== index))
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= rows.length) return
    const next = [...rows]
    const [moved] = next.splice(index, 1)
    next.splice(target, 0, moved!)
    commit(next)
  }

  const Wrapper = plain ? 'div' : 'section'

  return (
    <Wrapper className={plain ? 'dash-svc-setting-block' : 'dash-booking-settings-section'}>
      <header className={`dash-booking-fields-head${plain ? ' is-wizard' : ''}`}>
        <strong>
          {t(plain ? 'bookings.field.headWizard' : 'bookings.field.head')}
          {saving ? <IconLoader2 className="dash-spin" size={12} style={{ marginLeft: 6 }} /> : null}
        </strong>
        <p>
          {t(plain ? 'bookings.field.headBodyWizard' : 'bookings.field.headBody')}
          {rows.length ? ` ${t('bookings.field.count', { count: rows.length, max: MAX_FIELDS })}` : ''}
        </p>
      </header>

      <div className={plain ? undefined : 'dash-booking-settings-fields'}>
      {/* Sin lista, sólo el botón.
          Había además un recuadro con «Este paso es opcional» y un ejemplo, y
          era un cartel del tamaño de tres preguntas para decir que no hay
          ninguna —algo que ya se ve, porque no hay ninguna—. Que es opcional lo
          dice el paso; el ejemplo vive donde se usa, en el hueco del campo. */}
      {rows.length === 0 ? null : (
        <ol className="dash-booking-fields">
          {rows.map((field, index) => {
            const optionsKey = `${field.name}:options`
            return (
              <li key={field.name} className="dash-booking-field-row">
                {/* La pregunta se lleva el renglón entero: son hasta 80
                    caracteres y compartir fila con el tipo la dejaba escribiendo
                    en un hueco. El tipo baja al pie, donde con 170px sobra. */}
                <input
                  className="dash-booking-field-input"
                  value={field.label}
                  maxLength={80}
                  placeholder={t('bookings.field.placeholder')}
                  aria-label={t('bookings.field.labelAria', { index: index + 1 })}
                  /* Vacía no se puede guardar. Se dice con el filo del campo y
                     con esto —que es lo que lo cuenta a un lector de pantalla—
                     en vez de con un renglón de texto bajo cada tarjeta. */
                  aria-invalid={field.label.trim() === '' && (revealMissing || tocadas.has(field.name))}
                  /*
                   * La etiqueta se guarda EN CADA TECLA, no al salir del campo.
                   *
                   * Antes vivía en un borrador local y sólo subía en el `blur`,
                   * así que en el asistente de alta la vista previa no enseñaba
                   * la pregunta hasta que se pulsaba fuera: se escribía mirando
                   * un hueco vacío, que es justo lo que una vista previa existe
                   * para evitar.
                   *
                   * Sin recortar aquí: quien está escribiendo «¿Cuál es tu » va
                   * por el espacio, y recortarlo en cada tecla le borra el que
                   * acaba de poner. Se recorta al salir.
                   */
                  onChange={(event) => { replace(index, { label: event.target.value }) }}
                  onBlur={() => {
                    setTocadas((antes) => (antes.has(field.name) ? antes : new Set(antes).add(field.name)))
                    const value = field.label.trim()
                    /*
                     * El identificador se deriva de la etiqueta al SALIR, no
                     * mientras se escribe: derivarlo en cada tecla daría `c` a
                     * la primera letra de «Cuál es tu talla», y es la clave con
                     * la que se casa cada respuesta.
                     *
                     * Se renombra sólo mientras siga siendo el automático que
                     * puso «Añadir pregunta». En cuanto tiene un nombre salido
                     * de un texto, ya no se toca: renombrarlo bajo los pies de
                     * un formulario abierto pierde lo que estén rellenando.
                     */
                    const auto = /^pregunta(-\d+)?$/.test(field.name)
                    const taken = new Set(rows.filter((_, at) => at !== index).map((row) => row.name))
                    if (auto && value !== '') {
                      replace(index, { label: value, name: slugify(value, taken) })
                    } else if (value !== field.label) {
                      replace(index, { label: value })
                    }
                  }}
                />

                {field.type === 'select' ? (
                  <label className="dash-offer-field dash-booking-field-options">
                    <span className="dash-offer-field-label">{t('bookings.field.optionsLabel')}</span>
                    <textarea
                      rows={3}
                      placeholder={t('bookings.field.optionsPlaceholder')}
                      value={draftOf(optionsKey, field.options.join('\n'))}
                      onChange={(event) => setDraft(optionsKey, event.target.value)}
                      onBlur={() => {
                        const raw = draftOf(optionsKey, field.options.join('\n'))
                        clearDraft(optionsKey)
                        // Se limpian aquí y no al escribir para no borrar la línea
                        // vacía que alguien acaba de abrir con Enter.
                        const options = raw
                          .split('\n')
                          .map((option) => option.trim().slice(0, 60))
                          .filter(Boolean)
                          .slice(0, MAX_OPTIONS)
                        if (options.join('\n') !== field.options.join('\n')) replace(index, { options })
                      }}
                    />
                    {field.options.length === 0 ? (
                      <small className="dash-booking-field-warn">
                        {t('bookings.field.needsOptions')}
                      </small>
                    ) : null}
                  </label>
                ) : null}

                {/* Un solo renglón al pie: qué se pregunta, si hace falta
                    contestarla y —al otro extremo— moverla o quitarla. Las tres
                    cosas eran antes dos filas con un número entre medias. */}
                <div className="dash-booking-field-foot">
                  <SelectorDeTipo
                    value={field.type}
                    onChange={(type) => {
                      // Al dejar de ser lista, sus opciones dejan de significar
                      // algo: guardarlas escondidas las devolvería de vuelta si
                      // alguien cambia de idea dos veces.
                      const rest = type === 'select' ? {} : { options: [] }
                      // Un teléfono se pide para llamar, así que nace exigido.
                      // Es sólo el punto de partida: se puede desmarcar.
                      const required = type === 'tel' ? { required: true } : {}
                      replace(index, { type, ...rest, ...required })
                    }}
                  />

                  <label className="dash-booking-field-required">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(event) => replace(index, { required: event.target.checked })}
                    />
                    <span aria-hidden="true" />
                    <strong>{t('bookings.field.required')}</strong>
                  </label>

                  <div className="dash-booking-field-actions">
                    <button
                      type="button"
                      className="dash-booking-field-btn"
                      aria-label={t('bookings.field.moveUp')}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    ><IconChevronUp size={16} /></button>
                    <button
                      type="button"
                      className="dash-booking-field-btn"
                      aria-label={t('bookings.field.moveDown')}
                      disabled={index === rows.length - 1}
                      onClick={() => move(index, 1)}
                    ><IconChevronDown size={16} /></button>
                    <button
                      type="button"
                      className="dash-booking-field-btn is-danger"
                      aria-label={t('bookings.field.remove', { name: field.label || index + 1 })}
                      onClick={() => remove(index)}
                    ><IconTrash size={16} /></button>
                  </div>
                </div>

              </li>
            )
          })}
        </ol>
      )}

      <button
        type="button"
        className="dash-secondary-btn dash-booking-fields-add"
        disabled={rows.length >= MAX_FIELDS}
        onClick={add}
      >
        {/* «Añadir otra» sólo cuando ya hay una: siendo la primera, «otra» habla
            de algo que no existe. */}
        <IconPlus size={14} /> {t(rows.length === 0 || !plain ? 'bookings.field.add' : 'bookings.field.addWizard')}
      </button>
      </div>
    </Wrapper>
  )
}
